import React, { useState, Fragment } from "react";
import { Bar } from "react-chartjs-2";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSeedling,
  faSmog,
  faCoins,
  faFaucetDrip,
  faFaceSmile,
  faArrowTrendUp,
  faArrowTrendDown,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

Chart.register(ChartDataLabels);

const INDICATOR_META = {
  biodiversity:   { icon: faSeedling,  label: "biodiversity" },
  carbon_economy: { icon: faSmog,      label: "energySobriety" },
  economy:        { icon: faCoins,     label: "economicHealth" },
  popularity:     { icon: faFaceSmile, label: "popularity" },
  water_quality:  { icon: faFaucetDrip,label: "waterQuality" },
};

const Card = ({
  decision,
  selected,
  handleSelect,
  initialShortTermNeeds,
  round,
  showAdditionalScore,
  t,
}) => {
  const [flipped, setFlipped] = useState(false);

  const barChartData = {
    labels: [1, 2, 3, 4, 5].map((n) => `${t("Tour")} ${n}`),
    datasets: [{
      label: t("Economies d'eau"),
      backgroundColor: "#54a8ba",
      data: decision.water_saving,
    }],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        max: 12,
        title: { display: true, text: t("Économie d'eau") },
      },
    },
    plugins: {
      datalabels: {
        anchor: "end",
        align: "top",
        offset: -4,
        formatter: Math.round,
        font: { weight: "bold", size: 12 },
        color: "#205673",
      },
      legend: { display: false },
    },
  };

  const activeMetrics = Object.keys(decision.decision_metrics).filter(
    (k) => decision.decision_metrics[k] !== 0
  );

  return (
    <div
      className={`gm-card ${selected ? "gm-card--selected" : ""}`}
      onClick={() => { if (!flipped) handleSelect(decision); }}
    >
      {/* ── Front ── */}
      {!flipped && (
        <>
          {selected && (
            <div className="gm-card-selected-banner">✓ {t("Sélectionnée")}</div>
          )}
          <div
            className="gm-card-image"
            style={{
              backgroundImage: `url(${decision.image_url})`,
              backgroundPosition: decision.image_position || "50% 0px",
              backgroundSize: `${decision.image_zoom ?? 100}% auto`,
              backgroundRepeat: "no-repeat",
            }}
          />
          <button
            className="gm-detail-btn"
            onClick={(e) => { e.stopPropagation(); setFlipped(true); }}
            title={t("Voir les détails")}
          >
            i
          </button>
          <div className="gm-card-footer">
            <h3 className="gm-card-title">{decision.title}</h3>
            {round > 0 && showAdditionalScore && activeMetrics.length > 0 && (
              <div className="gm-card-indicators gm-card-indicators--centered">
                {activeMetrics.map((key) => {
                  const meta = INDICATOR_META[key];
                  if (!meta) return null;
                  const positive = decision.decision_metrics[key] > 0;
                  return (
                    <Fragment key={key}>
                      <Tooltip id={`tt-${decision._id}-${key}`} />
                      <span
                        className={`gm-indicator gm-indicator--front ${positive ? "gm-indicator--up" : "gm-indicator--down"}`}
                        data-tooltip-id={`tt-${decision._id}-${key}`}
                        data-tooltip-content={t(meta.label)}
                      >
                        <FontAwesomeIcon icon={positive ? faArrowTrendUp : faArrowTrendDown} className="gm-trend-icon" />
                        <FontAwesomeIcon icon={meta.icon} />
                      </span>
                    </Fragment>
                  );
                })}
              </div>
            )}
            <div className="gm-card-meta">
              {initialShortTermNeeds > 0 && (
                <span className="gm-card-water">
                  {decision.water_saving[0]}
                  <img className="gm-drop-icon" src="/hydros_logo.png" alt="water" />
                </span>
              )}
              <span className="gm-card-cost">
                {decision.cost}
                <img className="gm-currency-icon" src="/vote-white4.png" alt="votes" />
              </span>
            </div>
          </div>
        </>
      )}

      {/* ── Detail (flipped) ── */}
      {flipped && (
        <div className="gm-card-detail" onClick={(e) => e.stopPropagation()}>

          {/* Title + close */}
          <div className="gm-detail-header">
            <h3 className="gm-detail-title">{decision.title}</h3>
            <button className="gm-detail-close" onClick={() => setFlipped(false)}>×</button>
          </div>

          {/* Description */}
          <p className="gm-detail-desc">{decision.short_description}</p>

          {/* Bar chart */}
          <div className="gm-detail-bar">
            <Bar data={barChartData} options={barChartOptions} />
          </div>

          {/* Bottom: cost + water + select */}
          <div className="gm-detail-bottom">
            <div className="gm-detail-left">
              <span className="gm-card-cost gm-card-cost--dark">
                {decision.cost}
                <img className="gm-currency-icon" src="/vote-black4.png" alt="votes" />
              </span>
              {initialShortTermNeeds > 0 && (
                <span className="gm-card-water gm-card-water--dark">
                  {decision.water_saving[0]}
                  <img className="gm-drop-icon" src="/hydros_logo.png" alt="water" />
                </span>
              )}
              {round > 0 && showAdditionalScore && activeMetrics.length > 0 && (
                <div className="gm-card-indicators">
                  {activeMetrics.map((key) => {
                    const meta = INDICATOR_META[key];
                    if (!meta) return null;
                    const positive = decision.decision_metrics[key] > 0;
                    return (
                      <Fragment key={key}>
                        <Tooltip id={`tt2-${decision._id}-${key}`} />
                        <span
                          className={`gm-indicator ${positive ? "gm-indicator--up" : "gm-indicator--down"}`}
                          data-tooltip-id={`tt2-${decision._id}-${key}`}
                          data-tooltip-content={t(meta.label)}
                        >
                          <FontAwesomeIcon icon={positive ? faArrowTrendUp : faArrowTrendDown} className="gm-trend-icon" />
                          <FontAwesomeIcon icon={meta.icon} />
                        </span>
                      </Fragment>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              className={`gm-select-btn ${selected ? "gm-select-btn--selected" : ""}`}
              onClick={() => { handleSelect(decision); setFlipped(false); }}
            >
              {selected ? `✓ ${t("Sélectionnée")}` : t("Sélectionner")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Card;
